import { useState } from 'react';
import { Link } from "react-router-dom";
type Step = 'form' | 'confirm' | 'complete';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [agreed, setAgreed] = useState(false);
  const [step, setStep] = useState<Step>('form');
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);

  // 入力 → 確認
  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreed) {
      setError('プライバシーポリシーに同意してください');
      return;
    }
    setError('');
    setStep('confirm');
  };

  // 確認 → 送信
  const handleSend = async () => {
    setIsSending(true);
    setError('');

    try {
      const response = await fetch('/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setStep('complete');
        // フォーム内容リセット
        setFormData({
          name: '',
          email: '',
          message: ''
        });
        setAgreed(false);
      } else {
        setError('送信に失敗しました');
      }
    } catch {
      setError('送信に失敗しました');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl font-bold text-gray-800 mb-4"
              style={{ fontFamily: '"Noto Serif JP", serif' }}
            >
              お問い合わせ
            </h2>
            <div className="w-16 h-1 bg-green-600 mx-auto mb-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* 左：会社情報 */}
            <div className="bg-gray-50 border border-gray-300 p-6">
              <h3
                className="font-bold text-gray-800 mb-4 border-b border-gray-300 pb-2"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                会社情報
              </h3>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-green-700"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">所在地</p>
                    <p className="text-gray-700">
                      〒239-0831
                      <br />
                      神奈川県横須賀市ハイランド三丁目27番24号
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-green-700"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">営業時間</p>
                    <p className="text-gray-700">平日 9:00 - 18:00（土日祝休）</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 右：フォーム / 確認 / 完了 */}
            <div className="bg-gray-50 border border-gray-300 p-6">
              {step === 'form' && (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleConfirm}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-1">
                      お名前 <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-1">
                      メールアドレス <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-1">
                      お問い合わせ内容 <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-600 resize-none"
                    />
                  </div>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="privacy"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-1 cursor-pointer"
                    />
                    <label
                      htmlFor="privacy"
                      className="text-sm text-gray-700 cursor-pointer"
                    >
                      <Link to="/privacy" className="text-blue-600 underline">
                            プライバシーポリシー
                          </Link>
                      に同意する
                    </label>
                  </div>
                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                  <button
                    type="submit"
                    className="w-full bg-green-700 text-white py-2 font-bold hover:bg-green-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    入力内容を確認する
                  </button>
                </form>
              )}

              {step === 'confirm' && (
                <div className="space-y-4 text-sm">
                  <h3 className="font-bold text-gray-800 mb-2 border-b border-gray-300 pb-2">
                    入力内容の確認
                  </h3>
                  <p className="text-gray-700">
                    以下の内容でよろしければ「この内容で送信する」を押してください。
                  </p>

                  <div className="space-y-3 mt-4">
                    <div>
                      <p className="font-bold text-gray-800">お名前</p>
                      <p className="text-gray-700">{formData.name}</p>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">メールアドレス</p>
                      <p className="text-gray-700">{formData.email}</p>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">お問い合わせ内容</p>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {formData.message}
                      </p>
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}

                  <div className="flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setStep('form')}
                      className="w-1/2 border border-gray-300 text-gray-700 py-2 font-bold hover:bg-gray-100 transition-colors"
                    >
                      修正する
                    </button>
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={isSending}
                      className="w-1/2 bg-green-700 text-white py-2 font-bold hover:bg-green-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSending ? '送信中…' : 'この内容で送信する'}
                    </button>
                  </div>
                </div>
              )}

              {step === 'complete' && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-green-700"></i>
                  </div>
                  <p className="font-bold text-gray-800">送信完了</p>
                  <p className="text-sm text-gray-600 mt-2">
                    お問い合わせありがとうございます。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('form');
                      setError('');
                    }}
                    className="mt-4 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm text-gray-700 font-bold hover:bg-gray-100 transition-colors"
                  >
                    もう一度お問い合わせする
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
